import { type MouseEvent, useCallback, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAvatar,
  MiBadge,
  MiIcon,
  MiIconButton,
} from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { getInitials } from "@miplatform/utils";
import { mdiPlus } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { AvailabilityTimeBadge } from "./AvailabilityTimeBadge";

export const AssignableUserCard = ({
  name,
  availableTime = 4,
  assignedWorkOrderCount = 4,
  stagedWorkOrderCount = 0,
  selected = false,
  showAddButton = false,
  onClick,
  onAdd,
}: AssignableUserCardProps) => {
  const { classes, theme } = useStyles({
    clickable: !!onClick,
    selected,
  });
  const initials = useMemo(() => getInitials(name), [name]);
  const showStagingBadge = stagedWorkOrderCount > 0;
  const showAddAction = showAddButton && Boolean(onAdd);
  const stagedBadgeText =
    stagedWorkOrderCount > 99 ? "99+" : String(stagedWorkOrderCount);

  const handleAddClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onAdd?.();
    },
    [onAdd]
  );

  return (
    <Box className={classes.card} onClick={onClick}>
      <Box className={classes.header}>
        <Box className={classes.userInfo}>
          <MiAvatar className={classes.avatar}>{initials}</MiAvatar>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.CAPTION}
            fontWeight={theme.fontWeight.semiBold}
            color={theme.palette.text.primary}
            noWrap
          >
            {name}
          </Typography>
        </Box>
        <Box className={classes.headerActions}>
          {showStagingBadge ? (
            <MiBadge
              className={classes.stagingBadge}
              rounded
              text={stagedBadgeText}
              fontSize={11}
              backgroundColor={theme.palette.red.main}
              color={theme.palette.common.white}
              aria-label={`${stagedWorkOrderCount} work orders in staging`}
            />
          ) : null}
          {showAddAction ? (
            <MiIconButton
              onClick={handleAddClick}
              data-testid="add-assignable-user-button"
            >
              <MiIcon
                path={mdiPlus}
                size={0.6}
                color={theme.palette.grey.medium}
              />
            </MiIconButton>
          ) : null}
        </Box>
      </Box>
      <Box className={classes.badges}>
        <AvailabilityTimeBadge
          availableTime={availableTime}
          className={classes.badge}
        />
        <MiBadge
          className={classes.badge}
          backgroundColor={theme.palette.grey.mediumLight}
          color={theme.palette.text.primary}
          borderColor={theme.palette.grey.darkAccent}
          text={`${assignedWorkOrderCount} Work Orders`}
          fontSize={theme.fontSize.xsmall}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{
  clickable: boolean;
  selected: boolean;
}>()((theme, { clickable, selected }) => ({
  card: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    minWidth: 195,
    backgroundColor: selected
      ? theme.palette.primary.light
      : theme.palette.grey.light,
    border: `1px solid ${
      selected ? theme.palette.primary.main : theme.palette.grey.accent
    }`,
    borderRadius: theme.spacing(1),
    cursor: clickable ? "pointer" : "default",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minHeight: 24,
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
    flex: 1,
  },
  headerActions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    flexShrink: 0,
    gap: theme.spacing(0.5),
    height: 24,
  },
  avatar: {
    width: 18,
    height: 18,
    fontSize: theme.fontSize.xxsmall,
    backgroundColor: theme.palette.primary.main,
  },
  stagingBadge: {
    "&&": {
      flexShrink: 0,
      width: 18,
      height: 18,
      padding: theme.spacing(0),
      alignItems: "center",
      justifyContent: "center",
    },
  },
  badges: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
  },
  badge: {
    flex: 1,
    height: 14,
    alignItems: "center",
    justifyContent: "center",
    minWidth: 92,
  },
}));

interface AssignableUserCardProps {
  name: string;
  availableTime?: number;
  assignedWorkOrderCount?: number;
  stagedWorkOrderCount?: number;
  selected?: boolean;
  showAddButton?: boolean;
  onClick?: () => void;
  onAdd?: () => void;
}
